
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  MailboxInterface,
  MailboxRepositoryInterface, MailboxStatus,
  MailboxUserCreateInterface,
  MailboxUserInterface, MailboxUserUpdateInterface,
} from '@/services/Mailbox/interfaces';
import { DataTableHeader } from 'vuetify';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import EditMailboxUserDialog from '@/components/dialogs/mailbox/EditMailboxUserDialog.vue';
import DeleteMailboxDialog from '@/components/dialogs/mailbox/DeleteMailboxDialog.vue';
import CreateMailboxUserDialog from '@/components/dialogs/mailbox/CreateMailboxUserDialog.vue';
import DeleteMailboxUserDialog from '@/components/dialogs/mailbox/DeleteMailboxUserDialog.vue';
import ToggleMailboxDialog from '@/components/dialogs/mailbox/ToggleMailboxDialog.vue';
import SyncQuotasDialog from '@/components/dialogs/mailbox/SyncQuotasDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'mailboxRepository',
    'serverRepository',
    'websiteRepository',
  ],
  components: {
    EditMailboxUserDialog,
    DeleteMailboxDialog,
    CreateMailboxUserDialog,
    DeleteMailboxUserDialog,
    ToggleMailboxDialog,
    SyncQuotasDialog,
    PageHeader,
  },
})
export default class AllMailboxes extends Vue {
  public loading: boolean = true;

  public mailboxRepository!: MailboxRepositoryInterface;
  public serverRepository!: ServerRepositoryInterface;
  public websiteRepository!: WebsiteRepositoryInterface;

  public search: string = '';
  public mailboxes: MailboxInterface[] = [];
  public totalItems: number = 0;

  private serversMap: { [key: string]: ServerInterface } = {};
  public websitesMap: { [key: number]: Website } = {};

  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  public options: ListPaginationOptions = defaultListOptions();

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public userOptions: { page: number, itemsPerPage: number } = {
    page: 1,
    itemsPerPage: 5,
  };

  public mailboxesTableHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Account User', value: 'accountUser' },
    { text: 'Website', value: 'websiteId' },
    { text: 'Server', value: 'server' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions' },
    { text: '', value: 'data-table-expand' },
  ];

  private expanded: MailboxUserInterface[] = [];
  public userTableHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Address', value: 'address' },
    { text: 'Username', value: 'username' },
    { text: 'Real Name', value: 'realName' },
    { text: 'Directory', value: 'directory' },
    { text: 'Quota', value: 'quota.blockHard' },
    { text: 'Used', value: 'quota.used' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions' },
  ];


  public mailboxStatuses: string[] = [
    'CREATING',
    'CREATED',
    'UPDATING',
    'ERROR',
    'ACTIVE',
    'DISABLED',
  ];

  public userStatuses: string[] = this.mailboxStatuses;

  public createMailboxUserDialogData: {
    mailbox: MailboxInterface,
    website: Website,
  } | false = false;
  public createMailboxUserDialog: string = '';

  public deleteMailboxDialog: string = '';
  public mailboxForDeleteDialog: MailboxInterface | false = false;

  public deleteMailboxUserDialog: string = '';
  public mailboxUserForDeleteDialog: MailboxUserInterface | false = false;

  public editMailboxUserDialogData: {
    mailbox: MailboxInterface,
    website: Website,
    user: MailboxUserInterface,
    server: ServerInterface,
  } | false = false;
  public editMailboxUserDialog: string = '';

  public toggleMailboxDialog: string = '';

  public mailboxForToggleDialog: MailboxInterface | false = false;

  public domainForToggleDialog: string | false = false;

  public syncQuotasDialog: string = '';
  public serversForSyncDialog: ServerInterface[] = [];

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.initialize();
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public buildListQuery(): { [key: string]: string } {
    return buildListPaginationQuery(this.options);
  }

  public listQueryEquals(query: { [key: string]: string | (string | null)[] | null | undefined }): boolean {
    return listPaginationQueryEquals(query, this.options);
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = this.buildListQuery();
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'mailbox_list', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async onSearch(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.initialize();
  }

  public async onPageChange(): Promise<void> {
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.initialize();
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    } finally {
      this.loading = false;
    }
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.initialize();
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    } finally {
      this.loading = false;
    }
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'mailbox_list') {
      return;
    }
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.applyRouteQuery();
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    try {
      this.loading = true;
      await this.getMailboxes();
      await this.getServers();
      await this.getWebsites();
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    } finally {
      this.loading = false;
    }
  }

  public async getMailboxes(): Promise<void> {
    let search = this.search;
    const websiteSearch = Object.values(this.websitesMap).find((w) => w.domain === this.search.toString().trim());
    if (websiteSearch) {
      search = websiteSearch.id.toString();
    }

    [this.mailboxes, this.totalItems] = await this.mailboxRepository.searchMailbox(
      this.options.page,
      search,
      effectivePageSize(this.options.itemsPerPage),
    );
  }

  public async getServers(): Promise<void> {
    const ids = this.mailboxes.map((mailbox) => mailbox.server);
    if (!ids.length) {
      return;
    }

    const servers = await this.serverRepository.getServersByIds(ids);
    servers.forEach((server) => {
      this.serversMap[server._id] = server;
    });
  }

  public async getWebsites(): Promise<void> {
    const ids = this.mailboxes.map((mailbox) => mailbox.websiteId);
    if (!ids.length) {
      return;
    }

    const servers = await this.websiteRepository.getWebsitesByIds(ids);
    servers.forEach((website) => {
      this.websitesMap[website.id] = website;
    });
  }

  public createMailbox(): void {
    this.$router.push({ name: 'mailbox_create' });
  }

  public async editMailboxUser(user: MailboxUserInterface): Promise<void> {
    try {
      const updateData: MailboxUserUpdateInterface = {
        password: user.password ? user.password : undefined,
        realName: user.realName,
        aliases: user.aliases.map((mailboxAlias) => mailboxAlias.alias),
        forwards: user.forwards.map((forward) => forward.email),
        quota: {
          blockSoft: user.quota.blockSoft,
          blockHard: user.quota.blockHard,
        },
      };

      await this.mailboxRepository.updateMailboxUser(
        user.id,
        updateData,
      );

      this.$successDialog.show('Mailbox user update started!');
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  public async deleteMailbox(mailbox: MailboxInterface): Promise<void> {
    try {
      await this.mailboxRepository.deleteMailbox(mailbox.id);
      this.mailboxes = this.mailboxes.filter((m) => m.id !== mailbox.id);
      this.$successDialog.show('Mailbox Deletion Started');
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  public async deleteMailboxUser(user: MailboxUserInterface): Promise<void> {
    try {
      await this.mailboxRepository.deleteMailboxUser(user.id);
      this.$successDialog.show('Mailbox User Deletion Started');
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  public async showCreateMailboxUserDialog(mailbox: MailboxInterface): Promise<void> {
    if (!mailbox) {
      return;
    }

    this.createMailboxUserDialogData = {
      mailbox,
      website: this.websitesMap[mailbox.websiteId],
    };

    this.createMailboxUserDialog = 'create-mailbox-user-dialog';
  }

  public async createMailboxUser(userDTO: MailboxUserCreateInterface): Promise<void> {
    try {
      const user = await this.mailboxRepository.createMailboxUser(userDTO);
      const index = this.mailboxes.findIndex((mailbox) => mailbox.id === userDTO.mailboxId);
      this.$successDialog.show('Mailbox User Creation Started');

      if (index === -1) {
        return;
      }

      this.mailboxes[index].users.push(user);

    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  public showEditMailboxUserDialog(id: number): void {
    let mailbox: MailboxInterface = {} as MailboxInterface;
    let user: MailboxUserInterface = {} as MailboxUserInterface;

    this.mailboxes.forEach((mb) => {
      const mailboxUser = mb.users.find((mailboxUser) => mailboxUser.id === id);
      if (!mailboxUser) {
        return;
      }

      mailbox = mb;
      user = mailboxUser;
    });

    if (!mailbox.id || !user.id) {
      return;
    }

    this.editMailboxUserDialogData = {
      mailbox,
      user,
      server: this.serversMap[mailbox.server],
      website: this.websitesMap[mailbox.websiteId],
    };

    this.editMailboxUserDialog = 'edit-mailbox-user-dialog';
  }

  public showDeleteMailboxDialog(id: number): void {
    const mailbox = this.mailboxes.find((m) => m.id === id);
    if (!mailbox) {
      return;
    }

    this.mailboxForDeleteDialog = mailbox;
    this.deleteMailboxDialog = 'delete-mailbox-dialog';
  }

  public showDeleteMailboxUserDialog(user: MailboxUserInterface): void {
    this.mailboxUserForDeleteDialog = user;
    this.deleteMailboxUserDialog = 'delete-mailbox-user-dialog';
  }

  public showToggleDialog(mailbox: MailboxInterface): void {
    this.toggleMailboxDialog = 'toggle-mailbox-dialog';

    this.mailboxForToggleDialog = mailbox;
    this.domainForToggleDialog = this.websitesMap[mailbox.websiteId].domain;
  }

  public async toggleMailbox(mailbox: MailboxInterface): Promise<void> {
    try {
      const status = [1, 4].includes(mailbox.status) ? MailboxStatus.DISABLED : MailboxStatus.ACTIVE;

      const statusStr = status === MailboxStatus.DISABLED ? 'Disable' : 'Enable';

      await this.mailboxRepository.toggleMailbox(mailbox.id, status);

      this.$successDialog.show(`Mailbox ${statusStr} Started!`);
    } catch (err) {
      this.$errorDialog.show(err.message);
    }
  }

  public async showSyncQuotesDialog(): Promise<void> {
    this.serversForSyncDialog = await this.serverRepository.getAllServers();
    this.syncQuotasDialog = 'sync-quotas-dialog';
  }

  public async syncQuotas(server: string): Promise<void> {
    try {
      await this.mailboxRepository.syncQuotas(server);
    } catch (err) {
      this.$errorDialog.show(`Failed to start quotas sync for server ${server}`);
    }
  }
}
